/**
 * Health Status Page (Korat Health Style)
 *
 * Overview of health indicators, disease surveillance, and health service coverage
 */

import { useState } from 'react'
import { Heart, Activity, Shield, Users, AlertCircle } from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  Sidebar,
  PageHeader,
  KpiCardRich,
  HealthMapPolygon,
  YearFilter,
  DistrictFilter,
} from '../shared/components'
import { useDistricts, useHealthOverview, useHealthByDistrict, useHealthYearlyTrends } from '../services/api'
import { formatNumber } from '../shared/utils/formatNumber'

export function HealthPage() {
  const [selectedYear, setSelectedYear] = useState(2567)
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null)

  const { data: districts = [], isLoading: districtsLoading } = useDistricts()

  // Fetch health data based on district selection
  const overviewQuery = useHealthOverview(selectedYear)
  const districtQuery = useHealthByDistrict(selectedDistrict || 0, selectedYear)
  const trendsQuery = useHealthYearlyTrends({ districtId: selectedDistrict || undefined })

  // Type-safe data access
  const overviewData = overviewQuery.data
  const isLoading = selectedDistrict ? districtQuery.isLoading : overviewQuery.isLoading
  const trendsData = trendsQuery.data

  // Get selected district name for map filtering
  const selectedDistrictName = selectedDistrict
    ? districts.find((d) => d.id === selectedDistrict)?.name_th
    : null

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="health" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          icon={Heart}
          title="สถานะสุขภาพประชากร"
          showFilters={true}
          selectedDistrict={
            selectedDistrict ? districts.find((d) => d.id === selectedDistrict)?.name_th : null
          }
          filters={
            <>
              <YearFilter selectedYear={selectedYear} onYearChange={setSelectedYear} />
              <DistrictFilter
                selectedDistrict={selectedDistrict}
                onDistrictChange={setSelectedDistrict}
                districts={districts}
                isLoading={districtsLoading}
              />
            </>
          }
        />

        <div className="p-6">
          {/* Two-column layout: KPIs left, Map right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* Left Column: KPI Cards (40% = 2/5) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Health Coverage */}
              <KpiCardRich
                icon={Shield}
                title="อัตราการครอบคลุมบริการสุขภาพ"
                description="สัดส่วนประชากรที่เข้าถึงบริการสุขภาพพื้นฐาน"
                value={Math.min(overviewData?.coverage_rate || 98.5, 100)}
                unit="%"
                helperText={`เป้าหมาย ≥95% (WHO) | ${overviewData?.sso_members === 0 ? '⚠️ ข้อมูล SSO ยังไม่สมบูรณ์' : 'ครอบคลุม 3 สิทธิหลัก'}`}
                breakdown={[
                  `<b>หลักประกันสุขภาพ (UC)</b> ${formatNumber(overviewData?.uc_members || 0)}`,
                  `<b>ประกันสังคม (SSO)</b> ${formatNumber(overviewData?.sso_members || 0)}${overviewData?.sso_members === 0 ? ' <span style="color:#f59e0b;">⚠️</span>' : ''}`,
                  `<b>ข้าราชการ (CSMBS)</b> ${formatNumber(overviewData?.csmbs_members || 0)}`,
                ]}
                isLoading={isLoading}
                variant="navy"
              />

              {/* Active Patients */}
              <KpiCardRich
                icon={Users}
                title="ผู้ป่วยที่ได้รับการดูแล"
                description="จำนวนผู้รับบริการในระบบสุขภาพปัจจุบัน"
                value={overviewData?.active_patients || 0}
                unit="ราย"
                helperText="เฉลี่ย 420 รายต่อวัน"
                breakdown={[
                  `<b>OPD</b> ${formatNumber(overviewData?.opd_visits || 0)}`,
                  `<b>IPD</b> ${formatNumber(overviewData?.ipd_visits || 0)}`,
                  `<b>ER</b> ${formatNumber(overviewData?.er_visits || 0)}`,
                ]}
                isLoading={isLoading}
                variant="green"
              />

              {/* Disease Surveillance */}
              <KpiCardRich
                icon={AlertCircle}
                title="โรคที่ต้องเฝ้าระวัง"
                description="โรคติดต่ออันตรายและโรคระบาด (รายงานภายใน 24 ชม.)"
                value={overviewData?.surveillance_cases || 0}
                unit="ราย"
                helperText="เกณฑ์ความเสี่ยง: สูง >100 | กลาง 50-100 | ต่ำ <50 รายต่อเดือน"
                breakdown={[
                  `<b>ไข้เลือดออก</b> ${formatNumber(overviewData?.dengue || 0)}`,
                  `<b>มือเท้าปาก</b> ${formatNumber(overviewData?.hfmd || 0)}`,
                  `<b>อื่นๆ</b> ${formatNumber(overviewData?.other_diseases || 0)}`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />

              {/* Chronic Disease Management */}
              <KpiCardRich
                icon={Activity}
                title="โรคเรื้อรังที่ติดตาม"
                description="ผู้ป่วยโรคไม่ติดต่อเรื้อรัง (NCD) ในระบบ"
                value={overviewData?.ncd_patients || 0}
                unit="ราย"
                helperText="ติดตามต่อเนื่องทุกเดือน"
                breakdown={[
                  `<b>เบาหวาน</b> ${formatNumber(overviewData?.diabetes || 0)}`,
                  `<b>ความดันโลหิตสูง</b> ${formatNumber(overviewData?.hypertension || 0)}`,
                  `<b>หัวใจและหลอดเลือด</b> ${formatNumber(overviewData?.cvd || 0)}`,
                ]}
                isLoading={isLoading}
                variant="red"
              />
            </div>

            {/* Right Column: Map (60% = 3/5) */}
            <div className="lg:col-span-3">
              <div className="sticky top-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-korat-navy-700">
                    ภาพรวมสุขภาพตามเขตพื้นที่
                  </h2>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    แผนที่แสดงระดับความเสี่ยงและข้อมูลสุขภาพแยกตามอำเภอในจังหวัดนครราชสีมา
                  </p>
                </div>

                <HealthMapPolygon
                  year={selectedYear}
                  selectedDistrictId={selectedDistrict}
                  selectedDistrictName={selectedDistrictName}
                />
              </div>
            </div>

          </div>
          {/* End of two-column layout */}

          {/* Health Trends Chart - Full width below */}
          <section className="mt-6">
            <div className="bg-white rounded-xl border-2 border-korat-navy-200 shadow-sm p-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums">
                  แนวโน้มสถานะสุขภาพ 5 ปี (พ.ศ. 2563-2567)
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  จำนวนผู้ป่วยและอัตราครอบคลุมบริการสุขภาพในช่วง 5 ปีที่ผ่านมา
                </p>
              </div>

              {trendsQuery.isLoading ? (
                <div className="h-96 bg-slate-100 rounded animate-pulse" />
              ) : trendsData?.trends && trendsData.trends.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart
                    data={trendsData.trends.map((item) => ({
                      year: `${item.year}`,
                      cases: item.total_cases,
                      coverage: item.coverage_rate,
                    }))}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="year"
                    stroke="#64748b"
                    style={{ fontSize: '14px', fontFamily: 'IBM Plex Sans Thai' }}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#64748b"
                    style={{ fontSize: '14px', fontFamily: 'IBM Plex Sans Thai' }}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#64748b"
                    style={{ fontSize: '14px', fontFamily: 'IBM Plex Sans Thai' }}
                    tickFormatter={(value) => `${value}%`}
                    domain={[95, 100]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontFamily: 'IBM Plex Sans Thai',
                    }}
                    formatter={(value: number, name: string) => {
                      // Check by Thai display name (from Line component 'name' prop)
                      if (name === 'จำนวนผู้ป่วย') {
                        return [formatNumber(value) + ' ราย', 'จำนวนผู้ป่วย']
                      }
                      if (name === 'อัตราครอบคลุม (%)') {
                        return [value.toFixed(1) + '%', 'อัตราครอบคลุม']
                      }
                      return [value, name]
                    }}
                    labelFormatter={(label) => `ปี พ.ศ. ${label}`}
                  />
                  <Legend
                    wrapperStyle={{
                      fontFamily: 'IBM Plex Sans Thai',
                      fontSize: '14px',
                    }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="cases"
                    name="จำนวนผู้ป่วย"
                    stroke="#1e3a8a"
                    strokeWidth={3}
                    dot={{ fill: '#1e3a8a', r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="coverage"
                    name="อัตราครอบคลุม (%)"
                    stroke="#ea580c"
                    strokeWidth={3}
                    dot={{ fill: '#ea580c', r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <p>ไม่พบข้อมูลแนวโน้ม</p>
                  <p className="text-sm mt-2">กรุณาตรวจสอบข้อมูลในฐานข้อมูล</p>
                </div>
              )}

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>การวิเคราะห์:</strong> กราฟแสดงแนวโน้มจำนวนผู้ป่วยและอัตราครอบคลุมสิทธิประกันสุขภาพ (UC, SSS, CSMBS)
                  — ใช้ติดตามประสิทธิภาพระบบสาธารณสุข
                  <br />
                  <strong>อัตราครอบคลุมเฉลี่ย 5 ปี:</strong> {trendsData?.trends ?
                    (trendsData.trends.reduce((sum, item) => sum + item.coverage_rate, 0) / trendsData.trends.length).toFixed(2)
                    : '98.1'}% (เป้าหมาย WHO ≥ 95%)
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}
